import { DotLegalTable, ITableHeader, useDotLegalBreadCrumbs } from "@dotlegal/dotlegal-ui-components";
import { Box, InputAdornment, TableCell, TableRow, TextField } from "@mui/material";
import React, { Fragment, useCallback, useState } from "react";
import { useDebounceFunction } from "../../common/hooks/useDebounceFunction";
import Paper from "../../common/paper/Paper";
import { translateDateToString } from "../../core/cases/CaseService";
import { getDecimalValue, handleInput, IsValidDecimal, NumericFormatCustom } from "../../core/cases/CaseTableRow";
import { useUrlProvider } from "../../useUrlProvider";
import { useWatchPrices } from "./WatchPrices.hooks";
import { WatchPriceDto } from "./WatchPrices.types";

export interface IPriceCellProps {
    row: WatchPriceDto;
    onPriceChange: (watchPrice: { price: string | null; id: string }) => void;
}

function WatchPrices() {
    const { getAdminUrl } = useUrlProvider();
    useDotLegalBreadCrumbs([{ name: "Admin", link: getAdminUrl() }, { name: "Prices - Watches" }]);

    const { isLoading, data, updatePrice } = useWatchPrices();

    function getTableHeaders() {
        let headers = Array<ITableHeader<WatchPriceDto>>();
        headers.push({ property: "name", text: "Country", align: "left", showOnMobile: true, width: "100" });
        headers.push({ property: "price", text: "Price (DKK)", align: "left", showOnMobile: true, width: "6%" });
        headers.push({ property: "lastUpdated", text: "Latest update", align: "left", showOnMobile: true, width: "50" });
        headers.push({ property: "lastUpdated", text: "", align: "left", showOnMobile: true, width: "70%" });

        return headers;
    }

    const PriceCell = (props: IPriceCellProps): React.ReactElement => {
        const [text, setText] = useState<string | undefined>(getDecimalValue(props.row.price));
        const handlePriceChange = useCallback(
            (e: any) => {
                if (IsValidDecimal(e.target.value.toString())) {
                    setText(e.target.value);
                    props!.onPriceChange({
                        price: handleInput(e.target.value),
                        id: props.row.id,
                    });
                }
            },
            [props]
        );

        const debouncedHandlePriceChange = useDebounceFunction(handlePriceChange, 500);

        return (
            <TableCell sx={{ "& .MuiInputBase-root": { paddingLeft: "0px" } }}>
                {
                    <Fragment>
                        <TextField
                            id={props.row.id}
                            variant="standard"
                            sx={{
                                width: "80%",
                                "& fieldset": { border: "none" },
                                font: "#000000",
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000 !important",
                                },
                                "& .MuiInputAdornment-root": {
                                    "& .MuiTypography-root": {
                                        color: "#000000",
                                        fontSize: "0.75rem",
                                    },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "0.75rem",
                                    textAlign: "right",
                                    marginRight: "5px",
                                },
                                "& .MuiInput-underline": {
                                    ":after": {
                                        borderBottomColor: "#000000",
                                    },
                                    ":focus": {
                                        borderBottomColor: "#000000",
                                    },
                                    ":before": {
                                        border: "none",
                                    },
                                },
                            }}
                            onChange={debouncedHandlePriceChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment sx={{ color: "#000000" }} position="start">
                                        {text ? "DKK" : ""}
                                    </InputAdornment>
                                ),
                                inputComponent: NumericFormatCustom as any,
                            }}
                            inputProps={{
                                maxLength: 13,
                            }}
                            value={text}
                        />
                    </Fragment>
                }
            </TableCell>
        );
    };

    return (
        <Box
            sx={(theme) => ({
                "& .MuiTableCell-root": { paddingLeft: "6px !important" },
                minHeight: `calc(100vh - ${theme.spacingAsNumber(theme.customSpacing.contentSpacing) * 2 + 55}px)`,
            })}
        >
            <Paper fullHeight style={{ paddingBottom: "50px" }}>
                <DotLegalTable
                    headers={getTableHeaders()}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    data={data}
                    clickableRows={false}
                    renderRow={(row, i) => (
                        <TableRow key={i}>
                            <TableCell width="100px">{row.name}</TableCell>
                            <PriceCell
                                row={row}
                                onPriceChange={(watchPrice) => {
                                    updatePrice(watchPrice);
                                    row.price = watchPrice.price ? Number(watchPrice.price) : null;
                                }}
                            />
                            <TableCell>{translateDateToString(row.lastUpdated)}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    )}
                    paginationLabelOf={"of"}
                    hideRowsPerPage
                    hidePagination
                />
            </Paper>
        </Box>
    );
}

export default WatchPrices;
